/**
 * Paper Theater Scene — "Geometric Mesa": faceted low-poly mesa mountains,
 * geometric sun disc, cherry blossom petals, wireframe torii gates,
 * paper lanterns, cloud bands.
 */

import { Graphics } from 'pixi.js';
import type { StageScene, ParallaxLayers } from '../stage-scene';
import type { StageTheme } from '../../../stages';
import { WORLD } from '../../../constants';
import { FallingParticle } from '../particles/falling-particle';

interface Lantern { gfx: Graphics; x: number; y: number; baseY: number; phase: number; baseAlpha: number }
interface ToriiGate { gfx: Graphics; baseX: number; sway: number }

export class PaperTheaterScene implements StageScene {
  private petals: FallingParticle[] = [];
  private lanterns: Lantern[] = [];
  private toriiGates: ToriiGate[] = [];
  private sunGlow: Graphics | null = null;
  private fujiGlow: Graphics | null = null;
  private layers: ParallaxLayers | null = null;

  build(layers: ParallaxLayers, _theme: StageTheme): void {
    this.layers = layers;
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // ── Sky Layer: Night sky with geometric cloud bands ──

    // Dark warm sky
    const skyBg = new Graphics();
    skyBg.rect(0, 0, W, H);
    skyBg.fill({ color: 0x0a0608, alpha: 1.0 });
    layers.sky.container.addChild(skyBg);

    // Geometric sun disc (behind Fuji, upper right)
    const sunX = W * 0.65;
    const sunY = H * 0.12;

    // Sun outer glow
    this.sunGlow = new Graphics();
    this.sunGlow.circle(sunX, sunY, 120);
    this.sunGlow.fill({ color: 0xffcc44, alpha: 0.06 });
    this.sunGlow.circle(sunX, sunY, 70);
    this.sunGlow.fill({ color: 0xffdd66, alpha: 0.1 });
    layers.sky.container.addChild(this.sunGlow);

    // Sun disc body (bright geometric circle)
    const sunDisc = new Graphics();
    sunDisc.circle(sunX, sunY, 45);
    sunDisc.fill({ color: 0xffeecc, alpha: 0.3 });
    // Inner bright core
    sunDisc.circle(sunX, sunY, 25);
    sunDisc.fill({ color: 0xfffaee, alpha: 0.2 });
    layers.sky.container.addChild(sunDisc);

    // Radiating light beams from sun (12 thin rays)
    const sunRays = new Graphics();
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const innerR = 50;
      const outerR = 160 + Math.random() * 60;
      sunRays.moveTo(sunX + Math.cos(angle) * innerR, sunY + Math.sin(angle) * innerR);
      sunRays.lineTo(sunX + Math.cos(angle) * outerR, sunY + Math.sin(angle) * outerR);
    }
    sunRays.stroke({ color: 0xffd700, alpha: 0.06, width: 2 });
    layers.sky.container.addChild(sunRays);

    // Faint geometric cloud bands layered across sky
    for (let i = 0; i < 4; i++) {
      const cloud = new Graphics();
      const cy = H * (0.06 + i * 0.08);
      const cw = 300 + Math.random() * 200;
      const cx = W * (0.15 + i * 0.2) + (Math.random() - 0.5) * 100;
      // Geometric cloud — flat-topped trapezoid
      cloud.moveTo(cx - cw / 2, cy);
      cloud.lineTo(cx - cw * 0.3, cy - 8);
      cloud.lineTo(cx + cw * 0.3, cy - 8);
      cloud.lineTo(cx + cw / 2, cy);
      cloud.closePath();
      cloud.fill({ color: 0x332222, alpha: 0.08 + i * 0.01 });
      layers.sky.container.addChild(cloud);
    }

    // Sparse stars (50 — warm toned)
    for (let i = 0; i < 50; i++) {
      const star = new Graphics();
      const x = Math.random() * W;
      const y = Math.random() * H * 0.4;
      star.circle(x, y, 0.5 + Math.random() * 1);
      star.fill({ color: 0xffeedd, alpha: 0.15 + Math.random() * 0.3 });
      layers.sky.container.addChild(star);
    }

    // ── Deep Layer: Geometric Mesas ──
    // Multiple faceted low-poly mountains spread across the horizon

    const mesas = [
      { cx: W * 0.08, baseY: H * 0.62, peakY: H * 0.30, halfW: 180, peakHalfW: 22, alpha: 0.6 },
      { cx: W * 0.38, baseY: H * 0.60, peakY: H * 0.12, halfW: 320, peakHalfW: 35, alpha: 0.85 },
      { cx: W * 0.70, baseY: H * 0.63, peakY: H * 0.25, halfW: 240, peakHalfW: 28, alpha: 0.7 },
      { cx: W * 0.95, baseY: H * 0.61, peakY: H * 0.35, halfW: 200, peakHalfW: 20, alpha: 0.55 },
    ];

    for (const m of mesas) {
      this.drawMesa(layers, m.cx, m.baseY, m.peakY, m.halfW, m.peakHalfW, m.alpha);
    }

    // Base glow beneath the main (tallest) mesa
    this.fujiGlow = new Graphics();
    this.fujiGlow.ellipse(W * 0.38, H * 0.60, 200, 25);
    this.fujiGlow.fill({ color: 0xffd700, alpha: 0.04 });
    layers.deep.container.addChild(this.fujiGlow);

    // ── Mid Layer: Torii gates, petals, lanterns ──

    // 3 wireframe torii gates in parallax mid-layer
    const toriiPositions = [
      { x: W * 0.15, scale: 0.7 },
      { x: W * 0.55, scale: 0.5 },
      { x: W * 0.85, scale: 0.8 },
    ];
    for (const t of toriiPositions) {
      const gfx = new Graphics();
      const tx = t.x;
      const ty = H * 0.45;
      const tw = 50 * t.scale;
      const th = 80 * t.scale;

      // Wireframe torii — clean neon gold outlines, dark fill
      // Pillars
      gfx.rect(tx - tw / 2, ty, 4 * t.scale, th);
      gfx.rect(tx + tw / 2 - 4 * t.scale, ty, 4 * t.scale, th);
      gfx.fill({ color: 0x0a0604, alpha: 0.4 });
      // Top bar (kasagi)
      gfx.rect(tx - tw / 2 - 10 * t.scale, ty - 2, tw + 20 * t.scale, 5 * t.scale);
      gfx.fill({ color: 0x0a0604, alpha: 0.4 });
      // Upper crossbar (nuki)
      gfx.rect(tx - tw / 2 + 3, ty + 12 * t.scale, tw - 6, 3 * t.scale);
      gfx.fill({ color: 0x0a0604, alpha: 0.3 });

      // Gold wireframe outline
      // Left pillar outline
      gfx.rect(tx - tw / 2, ty, 4 * t.scale, th);
      gfx.stroke({ color: 0xffd700, alpha: 0.12, width: 1 });
      // Right pillar outline
      gfx.rect(tx + tw / 2 - 4 * t.scale, ty, 4 * t.scale, th);
      gfx.stroke({ color: 0xffd700, alpha: 0.12, width: 1 });
      // Top bar outline
      gfx.rect(tx - tw / 2 - 10 * t.scale, ty - 2, tw + 20 * t.scale, 5 * t.scale);
      gfx.stroke({ color: 0xffd700, alpha: 0.15, width: 1 });

      layers.mid.container.addChild(gfx);
      this.toriiGates.push({ gfx, baseX: tx, sway: 0 });
    }

    // Cherry blossom petals (45 — small geometric pentagons, pink/white)
    for (let i = 0; i < 45; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H;
      const size = 2 + Math.random() * 3;
      const isPink = Math.random() > 0.3;
      const color = isPink ? 0xff88aa : 0xffeedd;
      const alpha = 0.15 + Math.random() * 0.25;

      const petal = new FallingParticle(layers.mid.container, {
        x, y, size, color, alpha,
        speed: 15 + Math.random() * 25,
        drift: 8 + Math.random() * 15,
        driftPhase: Math.random() * Math.PI * 2,
      });

      // Override shape to be a pentagon
      petal.gfx.clear();
      for (let p = 0; p < 5; p++) {
        const angle = (p / 5) * Math.PI * 2 - Math.PI / 2;
        const px = Math.cos(angle) * size;
        const py = Math.sin(angle) * size;
        if (p === 0) petal.gfx.moveTo(px, py);
        else petal.gfx.lineTo(px, py);
      }
      petal.gfx.closePath();
      petal.gfx.fill({ color, alpha });

      // Add custom rotation properties (we'll just attach them dynamically for this specific scene)
      (petal as any).rotSpeed = 0.5 + Math.random() * 1.5;
      (petal as any).rot = Math.random() * Math.PI * 2;

      this.petals.push(petal);
    }

    // Paper lanterns — small glowing diamond shapes floating gently upward (12)
    for (let i = 0; i < 12; i++) {
      const gfx = new Graphics();
      const x = Math.random() * W;
      const y = H * 0.3 + Math.random() * H * 0.5;
      const size = 4 + Math.random() * 4;
      const baseAlpha = 0.15 + Math.random() * 0.15;

      // Diamond shape
      gfx.moveTo(0, -size);
      gfx.lineTo(size * 0.6, 0);
      gfx.lineTo(0, size);
      gfx.lineTo(-size * 0.6, 0);
      gfx.closePath();
      gfx.fill({ color: 0xffdd88, alpha: baseAlpha });
      // Warm glow halo
      gfx.circle(0, 0, size + 3);
      gfx.fill({ color: 0xffcc44, alpha: 0.04 });

      gfx.x = x;
      gfx.y = y;
      layers.mid.container.addChild(gfx);
      this.lanterns.push({ gfx, x, y, baseY: y, phase: Math.random() * Math.PI * 2, baseAlpha });
    }
  }

  /** Draw a single faceted low-poly mesa at the given position/scale. */
  private drawMesa(
    layers: ParallaxLayers, cx: number, baseY: number, peakY: number,
    halfW: number, peakHalfW: number, alpha: number,
  ): void {
    const left = cx - halfW;
    const right = cx + halfW;

    // Vertex layout: base-left, mid-left slopes, flat-top peak, mid-right slopes, base-right
    const verts = [
      { x: left, y: baseY },
      { x: left + halfW * 0.3, y: baseY - (baseY - peakY) * 0.15 },
      { x: cx - halfW * 0.55, y: baseY - (baseY - peakY) * 0.35 },
      { x: cx - halfW * 0.35, y: baseY - (baseY - peakY) * 0.6 },
      { x: cx - peakHalfW * 1.5, y: peakY + (baseY - peakY) * 0.1 },
      // Flat-top peak
      { x: cx - peakHalfW, y: peakY },
      { x: cx + peakHalfW, y: peakY },
      // Right side
      { x: cx + peakHalfW * 1.5, y: peakY + (baseY - peakY) * 0.1 },
      { x: cx + halfW * 0.35, y: baseY - (baseY - peakY) * 0.6 },
      { x: cx + halfW * 0.55, y: baseY - (baseY - peakY) * 0.35 },
      { x: right - halfW * 0.3, y: baseY - (baseY - peakY) * 0.15 },
      { x: right, y: baseY },
    ];

    // Filled body
    const body = new Graphics();
    body.moveTo(verts[0].x, verts[0].y);
    for (const v of verts) body.lineTo(v.x, v.y);
    body.closePath();
    body.fill({ color: 0x0a0808, alpha });
    layers.deep.container.addChild(body);

    // Gold wireframe — outer silhouette + internal facets
    const wire = new Graphics();
    for (let i = 0; i < verts.length - 1; i++) {
      wire.moveTo(verts[i].x, verts[i].y);
      wire.lineTo(verts[i + 1].x, verts[i + 1].y);
    }
    // Internal facet lines
    const internals: [number, number][] = [
      [0, 3], [1, 3], [2, 4], [3, 5], [4, 5],
      [6, 7], [6, 8], [7, 9], [8, 10], [9, 11],
      [2, 3], [3, 4], [8, 9], [9, 10],
    ];
    for (const [a, b] of internals) {
      if (a < verts.length && b < verts.length) {
        wire.moveTo(verts[a].x, verts[a].y);
        wire.lineTo(verts[b].x, verts[b].y);
      }
    }
    wire.stroke({ color: 0xffd700, alpha: alpha * 0.18, width: 1.5 });
    layers.deep.container.addChild(wire);
  }

  update(dt: number, elapsed: number): void {
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // Sun glow pulse
    if (this.sunGlow) {
      this.sunGlow.alpha = 0.8 + 0.2 * Math.sin(elapsed * 0.5);
    }

    // Fuji base glow shimmer
    if (this.fujiGlow) {
      this.fujiGlow.alpha = 0.7 + 0.3 * Math.sin(elapsed * 0.7);
    }

    // Torii gate sway decay
    for (const gate of this.toriiGates) {
      gate.sway *= Math.pow(0.94, dt * 60);
      gate.gfx.x = gate.baseX + Math.sin(elapsed * 1.0) * 1 + gate.sway;
    }

    // Cherry blossom petals — drift down with lateral sway and rotation
    for (const petal of this.petals) {
      petal.update(dt, elapsed, W, H);

      // Handle custom rotation
      const dynPetal = petal as any;
      dynPetal.rot += dynPetal.rotSpeed * dt;
      petal.gfx.rotation = dynPetal.rot;
    }

    // Paper lanterns — gentle upward float + warm flicker
    for (const lantern of this.lanterns) {
      lantern.y -= 3 * dt; // Very slow upward drift
      if (lantern.y < -20) {
        lantern.y = H + 20;
        lantern.x = Math.random() * W;
        lantern.baseY = lantern.y;
      }
      lantern.gfx.x = lantern.x + Math.sin(elapsed * 0.5 + lantern.phase) * 4;
      lantern.gfx.y = lantern.y;
      lantern.gfx.alpha = lantern.baseAlpha * (0.7 + 0.3 * Math.sin(elapsed * 2 + lantern.phase));
    }
  }

  onExplosion(worldX: number, _worldY: number, _radius: number): void {
    // Torii gates sway on explosion
    for (const gate of this.toriiGates) {
      const dx = worldX - gate.baseX;
      const dist = Math.abs(dx);
      const falloff = Math.max(0, 1 - dist / 800);
      gate.sway += Math.sign(-dx) * falloff * 12;
    }
    // Brief petal scatter
    for (const petal of this.petals) {
      petal.driftPhase += (Math.random() - 0.5) * 2;
    }
  }

  destroy(): void {
    this.petals = [];
    this.lanterns = [];
    this.toriiGates = [];
    this.sunGlow = null;
    this.fujiGlow = null;
    if (this.layers) {
      this.layers.sky.clear();
      this.layers.deep.clear();
      this.layers.mid.clear();
    }
  }
}
